import { Observable, pipe, distinctUntilChanged, filter, map, OperatorFunction, scan } from 'rxjs';
import { createFeatureSelector, createSelector, select } from '@ngrx/store';
import { ofType } from '@ngrx/effects';

import {
  CasLoginStatus,
  CasProfile,
  LoginAccessToken,
  LoginPhase,
  LoginPhaseError,
  LoginPhaseProfile,
  LoginState,
  LoginStatus,
  Profile,
  ProfileType,
} from '@fcom/core-api/login/interfaces/login.interface';
import {
  AccountSecurityFlow,
  AccountSecurityFlowAction,
  AccountSecurityFlowData,
  AccountSecurityFlowType,
  AccountSecurityStandaloneAction,
  AccountSecurityMfa,
  AccountSecurityFlowStep,
  AccountSecurityActionName,
} from '@fcom/core-api/login/interfaces/account-security.interface';
import { finShare, safeMap } from '@fcom/rx';

import { BaseState } from '../interfaces/app-state.interface';
import { safeSelect, StateSelector } from './selector-utils';
import {
  LoginActionUnion,
  setAccountSecurityFlowAction,
  setAccountSecurityStandaloneAction,
} from '../actions/login.actions';

const ACCESS_TOKEN: keyof LoginState = 'accessToken';
const TOKEN: keyof LoginAccessToken = 'token';

export const loginSelector = createFeatureSelector<LoginState>('login');
export const loginStatusSelector = createSelector(loginSelector, (l) => l.loginStatus);
export const casLoginStatusSelector = createSelector(loginSelector, (l) => l.casLoginStatus);
export const profileTypeSelector = createSelector(loginSelector, (l) => l?.type || undefined);
export const profileSelector = createSelector(loginSelector, (l) => l.profile);
export const isJuniorMemberSelector = createSelector(profileSelector, (p) => !!p?.isJuniorMember);
export const casProfileSelector = createSelector(loginSelector, (l) => l.casProfile);
export const accessTokenSelector = createSelector(loginSelector, (l) => l?.[ACCESS_TOKEN] || undefined);
export const tokenSelector = createSelector(accessTokenSelector, (l) => {
  return l?.[TOKEN] || undefined;
});
export const loginPhaseSelector = createSelector(loginSelector, (l) => l?.loginPhase || undefined);
export const loginPhaseProfileSelector = createSelector(loginPhaseSelector, (phase) => phase?.profile);
export const loginPhaseErrorSelector = createSelector(loginPhaseSelector, (phase) => phase?.error);

export const accountSecuritySelector = createSelector(loginSelector, (l) => l.accountSecurity);
export const accountSecurityMfaSelector = createSelector(accountSecuritySelector, (l) => l.mfa);
export const accountSecurityMfaIsEnabledSelector = createSelector(accountSecurityMfaSelector, (l) => l.isEnabled);
export const accountSecurityLastStandaloneActionSelector = createSelector(
  accountSecuritySelector,
  (l) => l.lastStandaloneAction
);
export const accountSecurityLastStandaloneActionIsLoadingSelector = createSelector(
  accountSecurityLastStandaloneActionSelector,
  (l) => l.isLoading
);
export const accountSecurityFlowSelector = createSelector(accountSecuritySelector, (l) => l.flow);

export const loginState = (): StateSelector<BaseState, LoginState> => safeSelect(loginSelector);
export const loginStatus = (): StateSelector<BaseState, LoginStatus> =>
  pipe(safeSelect(loginStatusSelector), distinctUntilChanged());
export const casLoginStatus = (): StateSelector<BaseState, CasLoginStatus> =>
  pipe(safeSelect(casLoginStatusSelector), distinctUntilChanged());

export const activeLogin = (): StateSelector<BaseState, LoginStatus> =>
  pipe(
    // LoginStatus on active login: INITIAL → NOT_LOGGED_IN → PENDING → LOGGED_IN
    // LoginStatus on page reload when already logged in: INITIAL → PENDING → LOGGED_IN
    // LoginStatus on silent token refresh while logged in: LOGGED_IN → PENDING → LOGGED_IN
    loginStatus(),
    scan(
      (acc, curr) => {
        if (curr === LoginStatus.NOT_LOGGED_IN) {
          return { wasNotLoggedIn: true, emit: false };
        }
        if (curr === LoginStatus.LOGGED_IN) {
          return { wasNotLoggedIn: false, emit: acc.wasNotLoggedIn };
        }
        return { wasNotLoggedIn: acc.wasNotLoggedIn, emit: false };
      },
      { wasNotLoggedIn: false, emit: false }
    ),
    filter(({ emit }) => emit),
    map(() => LoginStatus.LOGGED_IN)
  );

export const profileType = (): StateSelector<BaseState, ProfileType> => select(profileTypeSelector);

export const loginPhase = (): StateSelector<BaseState, LoginPhase> => select(loginPhaseSelector);
export const loginPhaseProfile = (): StateSelector<BaseState, LoginPhaseProfile> => select(loginPhaseProfileSelector);
export const loginPhaseError = (): StateSelector<BaseState, LoginPhaseError> => select(loginPhaseErrorSelector);

/** Profile, waits until we have one. Waits until user is logged in. See also profileOrUndefinedIfNotLoggedIn */
export const profile = (): StateSelector<BaseState, Profile> =>
  pipe(safeSelect(profileSelector), distinctUntilChanged());

/** Profile if logged in, undefined if not ready yet or if not logged in */
export const profileOrNot = (): StateSelector<BaseState, Profile | undefined> => select(profileSelector);

/** False until a profile is loaded */
export const isJuniorMember = (): StateSelector<BaseState, boolean> => select(isJuniorMemberSelector);

/** casProfile, waits until we have one. Waits until user is logged in. See also casProfileOrUndefinedIfNotLoggedIn */
export const casProfile = (): StateSelector<BaseState, CasProfile> =>
  pipe(safeSelect(casProfileSelector), distinctUntilChanged());

export const casProfileOrNot = (): StateSelector<BaseState, CasProfile | undefined> => select(casProfileSelector);

/** Token from the LoginAccessToken. Can be undefined. */
export const loginToken = (): StateSelector<BaseState, string> => select(tokenSelector);

/**
 * Signal counterpart of profileOrUndefinedIfNotLoggedIn
 */
export const selectProfileOrUndefinedIfNotLoggedIn = createSelector(loginSelector, (l) =>
  l.loginStatus === LoginStatus.LOGGED_IN ||
  l.loginStatus === LoginStatus.NOT_LOGGED_IN ||
  l.loginStatus === LoginStatus.ERROR
    ? l.profile
    : undefined
);

/**
 * Profile, waits over the INITIAL and PENDING states to know if we have a profile,
 * then returns either the profile or undefined
 */
export const profileOrUndefinedIfNotLoggedIn =
  () =>
  (state$: Observable<BaseState>): Observable<Profile | undefined> =>
    state$.pipe(
      loginState(),
      filter(
        (l: LoginState) =>
          l.loginStatus === LoginStatus.LOGGED_IN ||
          l.loginStatus === LoginStatus.NOT_LOGGED_IN ||
          l.loginStatus === LoginStatus.ERROR
      ),
      safeMap((l: LoginState) => l.profile), // l.profile is undefined if not logged in
      finShare()
    );

export const casProfileOrUndefinedIfNotLoggedIn =
  () =>
  (state$: Observable<BaseState>): Observable<CasProfile | undefined> =>
    state$.pipe(
      loginState(),
      filter(
        (l: LoginState) =>
          l.casLoginStatus === CasLoginStatus.LOGGED_IN ||
          l.casLoginStatus === CasLoginStatus.NOT_LOGGED_IN ||
          l.casLoginStatus === CasLoginStatus.ERROR
      ),
      safeMap((l: LoginState) => l.casProfile), // l.casProfile is undefined if not logged in
      finShare()
    );

/**
 * Waits until the profile is available, if logged in and has a first name and last name they are returned otherwise null
 */
export const profileName =
  () =>
  (state$: Observable<BaseState>): Observable<string | null> =>
    state$.pipe(
      profileOrNot(),
      filter(Boolean),
      map((profileData: Profile) => {
        if (profileData.firstname && profileData.lastname) {
          return `${profileData.firstname} ${profileData.lastname}`;
        }

        return null;
      })
    );

export const casProfileName =
  () =>
  (state$: Observable<BaseState>): Observable<string | null> =>
    state$.pipe(
      casProfileOrNot(),
      filter(Boolean),
      map((profileData: CasProfile) => {
        if (profileData.firstName && profileData.lastName) {
          return `${profileData.firstName} ${profileData.lastName}`;
        }

        return null;
      })
    );

export const loginIsOpen =
  () =>
  (state$: Observable<BaseState>): Observable<boolean> =>
    state$.pipe(
      loginPhase(),
      map((phase) => !!phase?.step),
      finShare(),
      distinctUntilChanged()
    );

export const accountSecurityMfa = (): StateSelector<BaseState, AccountSecurityMfa> =>
  select(accountSecurityMfaSelector);

export const accountSecurityMfaIsEnabled = (): StateSelector<BaseState, boolean> =>
  select(accountSecurityMfaIsEnabledSelector);

export const accountSecurityLastStandaloneAction = (): StateSelector<BaseState, AccountSecurityStandaloneAction> =>
  select(accountSecurityLastStandaloneActionSelector);

export const accountSecurityLastStandaloneActionIsLoading = (): StateSelector<BaseState, boolean> =>
  select(accountSecurityLastStandaloneActionIsLoadingSelector);

export const accountSecurityFlowMap = (): StateSelector<
  BaseState,
  Record<AccountSecurityFlowType, AccountSecurityFlow<AccountSecurityFlowType>>
> => select(accountSecurityFlowSelector);

export const accountSecurityFlow =
  <T extends AccountSecurityFlowType>(flowType: T) =>
  (state$: Observable<BaseState>): Observable<AccountSecurityFlow<T>> =>
    state$.pipe(
      accountSecurityFlowMap(),
      map((flowActionMap) => flowActionMap[flowType] as AccountSecurityFlow<T>),
      distinctUntilChanged()
    );

export const accountSecurityFlowStep =
  <T extends AccountSecurityFlowType>(flowType: T) =>
  (state$: Observable<BaseState>): Observable<AccountSecurityFlowStep<T>> =>
    state$.pipe(
      accountSecurityFlow(flowType),
      map((flow) => flow.step),
      distinctUntilChanged()
    );

export const accountSecurityFlowLastAction =
  <T extends AccountSecurityFlowType>(flowType: T) =>
  (state$: Observable<BaseState>): Observable<AccountSecurityFlowAction<T>> =>
    state$.pipe(
      accountSecurityFlow(flowType),
      map((flow) => flow.lastAction as AccountSecurityFlowAction<T>),
      distinctUntilChanged()
    );

export const accountSecurityFlowLastActionIsLoading =
  <T extends AccountSecurityFlowType>(flowType: T) =>
  (state$: Observable<BaseState>): Observable<boolean> =>
    state$.pipe(
      accountSecurityFlowLastAction(flowType),
      map((lastAction) => lastAction.isLoading),
      distinctUntilChanged()
    );

export const accountSecurityFlowData =
  <T extends AccountSecurityFlowType>(flowType: T) =>
  (state$: Observable<BaseState>): Observable<AccountSecurityFlowData<T>> =>
    state$.pipe(
      accountSecurityFlow(flowType),
      map((flow) => flow.data),
      distinctUntilChanged()
    );

export type AccountSecurityStandaloneActionTriggeredOptions = {
  actionName: AccountSecurityActionName;
  isImmediate?: boolean;
};

export type AccountSecurityStandaloneActionResponseHandlerOptions = {
  actionName: AccountSecurityActionName;
};

export const accountSecurityStandaloneActionTriggered = ({
  actionName,
  isImmediate,
}: AccountSecurityStandaloneActionTriggeredOptions): OperatorFunction<
  LoginActionUnion,
  AccountSecurityStandaloneAction
> => filterAccountSecurityStandaloneAction(actionName, !isImmediate);

export const accountSecurityStandaloneActionResponseHandler = ({
  actionName,
}: AccountSecurityStandaloneActionResponseHandlerOptions): OperatorFunction<
  LoginActionUnion,
  AccountSecurityStandaloneAction
> => filterAccountSecurityStandaloneAction(actionName, false);

const filterAccountSecurityStandaloneAction = (
  actionName: AccountSecurityActionName,
  isLoading: boolean
): OperatorFunction<LoginActionUnion, AccountSecurityStandaloneAction> => {
  return pipe(
    ofType(setAccountSecurityStandaloneAction),
    filter(({ standaloneAction }) => {
      return standaloneAction.name === actionName && standaloneAction.isLoading === isLoading;
    }),
    map(
      ({ standaloneAction }) =>
        ({
          ...standaloneAction,
          success: !standaloneAction.isLoading && !standaloneAction.error,
        }) as AccountSecurityStandaloneAction
    )
  );
};

export type AccountSecurityFlowActionTriggeredOptions<T extends AccountSecurityFlowType = undefined> = {
  flowType: T;
} & AccountSecurityStandaloneActionTriggeredOptions;

export type AccountSecurityFlowActionResponseHandlerOptions<T extends AccountSecurityFlowType = undefined> = {
  flowType: T;
} & AccountSecurityStandaloneActionResponseHandlerOptions;

export const accountSecurityFlowActionTriggered = <T extends AccountSecurityFlowType>({
  flowType,
  actionName,
  isImmediate,
}: AccountSecurityFlowActionTriggeredOptions<T>): OperatorFunction<LoginActionUnion, AccountSecurityFlowAction<T>> =>
  filterAccountSecurityFlowAction<T>(flowType, actionName, !isImmediate);

export const accountSecurityFlowActionResponseHandler = <T extends AccountSecurityFlowType>({
  flowType,
  actionName,
}: AccountSecurityFlowActionResponseHandlerOptions<T>): OperatorFunction<
  LoginActionUnion,
  AccountSecurityFlowAction<T>
> => filterAccountSecurityFlowAction<T>(flowType, actionName, false);

const filterAccountSecurityFlowAction = <T extends AccountSecurityFlowType>(
  flowType: T,
  actionName: AccountSecurityActionName,
  isLoading: boolean
): OperatorFunction<LoginActionUnion, AccountSecurityFlowAction<T>> => {
  return pipe(
    ofType(setAccountSecurityFlowAction),
    filter((action) => {
      return (
        action.flowType === flowType &&
        action.flowAction.name === actionName &&
        action.flowAction.isLoading === isLoading
      );
    }),
    map(
      ({ flowAction }) =>
        ({ ...flowAction, success: !flowAction.isLoading && !flowAction.error }) as AccountSecurityFlowAction<T>
    )
  );
};
